import { useSyncExternalStore } from 'react';

/**
 * Språk: Svenska / English. Ett presentationsval per installation (localStorage, nyckeln nedan), precis
 * som temat: det synkas inte med kontot och påverkas inte av inloggning, utloggning eller profilbyte.
 *
 * Sanningskälla och migrering (settings.locale i kalenderdatan används inte längre för presentation):
 *  1. Sparat val i 'autoday.language' gäller alltid.
 *  2. Inget sparat val + befintlig installation (data fanns före språkstödet) → svenska, sparas.
 *  3. Ny installation → första språk i navigator.languages som stöds (sv/en), annars engelska. Sparas.
 * Språkbyte ändrar aldrig tidszon, klockslag, återkomster, larm, användarens text eller kalendernamn.
 * public/theme-boot.js sätter <html lang> enligt samma regler innan något ritas.
 */
export type Lang = 'sv' | 'en';

export const LANG_KEY = 'autoday.language';
export const LANGS: ReadonlyArray<{ value: Lang; label: string }> = [
  { value: 'sv', label: 'Svenska' },
  { value: 'en', label: 'English' },
];

export function isLang(v: unknown): v is Lang {
  return v === 'sv' || v === 'en';
}

/** Första stödda språk i webbläsarens lista (sv-FI → sv, en-US → en), annars engelska. */
export function detectLang(languages: ReadonlyArray<string> | undefined | null): Lang {
  for (const tag of languages ?? []) {
    const primary = String(tag).toLowerCase().split(/[-_]/)[0];
    if (primary === 'sv' || primary === 'en') return primary;
  }
  return 'en';
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readStoredLang(storage: () => Storage | null = defaultStorage): Lang | null {
  try {
    const v = storage()?.getItem(LANG_KEY);
    return isLang(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredLang(lang: Lang, storage: () => Storage | null = defaultStorage): boolean {
  try {
    const s = storage();
    if (!s) return false;
    s.setItem(LANG_KEY, lang);
    return s.getItem(LANG_KEY) === lang;
  } catch {
    return false;
  }
}

function navigatorLanguages(): string[] {
  try {
    if (typeof navigator === 'undefined') return [];
    return navigator.languages?.length ? [...navigator.languages] : navigator.language ? [navigator.language] : [];
  } catch {
    return [];
  }
}

/** BCP 47-tagg för Intl och FullCalendar. Engelska utan region: datum skrivs ut med månadsnamn, 24 h. */
export const BCP47: Record<Lang, string> = { sv: 'sv-SE', en: 'en' };

interface LangSnapshot {
  lang: Lang;
  /** false = valet kunde inte sparas (blockerad lagring) och gäller bara den här sessionen. */
  persisted: boolean;
}

class LangController {
  private snap: LangSnapshot;
  private listeners = new Set<() => void>();
  private started = false;

  constructor() {
    const stored = readStoredLang();
    const fromBoot = typeof document !== 'undefined' ? document.documentElement.getAttribute('data-lang-pref') : null;
    this.snap = { lang: stored ?? (isLang(fromBoot) ? fromBoot : detectLang(navigatorLanguages())), persisted: stored !== null };
  }

  get snapshot(): LangSnapshot {
    return this.snap;
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  /** Körs en gång vid start, innan något ritas. Se reglerna överst i filen. */
  async start(existingInstall: () => Promise<boolean>): Promise<void> {
    if (this.started) return;
    this.started = true;
    let lang = readStoredLang();
    let persisted = lang !== null;
    if (!lang) {
      let existing = true;
      try {
        existing = await existingInstall();
      } catch {
        existing = true; // vid osäkerhet: behåll svenska som förut
      }
      lang = existing ? 'sv' : detectLang(navigatorLanguages());
      persisted = writeStoredLang(lang);
    }
    this.set(lang, persisted);
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key === LANG_KEY && isLang(e.newValue)) this.set(e.newValue, true);
      });
    }
  }

  choose(lang: Lang): void {
    this.set(lang, writeStoredLang(lang));
  }

  private set(lang: Lang, persisted: boolean) {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
      document.documentElement.removeAttribute('data-lang-pref');
    }
    if (lang === this.snap.lang && persisted === this.snap.persisted) return;
    this.snap = { lang, persisted };
    for (const l of this.listeners) l();
  }
}

let controller: LangController | null = null;
export function getLang(): LangController {
  return (controller ??= new LangController());
}

/** Aktuellt språk (utanför React, t.ex. i meddelanden från asynkron kod). */
export function currentLang(): Lang {
  return getLang().snapshot.lang;
}

/** Text på aktuellt språk. Båda språken står bredvid varandra där texten används. */
export function tr(sv: string, en: string): string {
  return currentLang() === 'en' ? en : sv;
}

/** Svensk/engelsk pluralform: plural(n, ['dag', 'dagar'], ['day', 'days']) → "1 dag" / "3 days". */
export function plural(n: number, sv: [string, string], en: [string, string]): string {
  const forms = currentLang() === 'en' ? en : sv;
  return `${n} ${n === 1 ? forms[0] : forms[1]}`;
}

/** Komponenter som visar text anropar denna så att de ritas om direkt vid språkbyte. */
export function useLang(): LangSnapshot & { t: typeof tr; locale: string } {
  const c = getLang();
  const snap = useSyncExternalStore(c.subscribe, () => c.snapshot, () => c.snapshot);
  return { ...snap, t: tr, locale: BCP47[snap.lang] };
}
